import React, { Fragment } from 'react';

import { Page } from '../../../payload/payload-types.js';

import { AccordionBlockGraphQL } from '@/app/_blocks/AccordionBlockGraphQL';
import { ApplicationHeroGraphQL } from '@/app/_blocks/ApplicationHeroGraphQL';
import { BeforeAndAfterGraphQL } from '@/app/_blocks/BeforeAndAfterGraphQL';
import { ComparisonTableGraphQL } from '@/app/_blocks/ComparisonTableGraphQL';
import { ContentBlock } from '@/app/_blocks/Content';
import { FeaturesBlockGraphQL } from '@/app/_blocks/FeaturesBlockGraphQL';
import { FormBlockGraphQL } from '@/app/_blocks/FormBlockGraphQL';
import { GalleryBlockGraphQL } from '@/app/_blocks/GalleryBlockGraphQL';
import { GalleryPageGraphQL } from '@/app/_blocks/GalleryPageGraphQL';
import { HomepageHeroGraphQL } from '@/app/_blocks/HomepageHeroGraphQL';
import { IframeBlockGraphQL } from '@/app/_blocks/IframeBlockGraphQL';
import { KontactGraphQL } from '@/app/_blocks/KontactGraphQL';
import { MatchingProductsGraphQL } from '@/app/_blocks/MatchingProductsGraphQL';
import { MediaBlock } from '@/app/_blocks/MediaBlock';
import { MusterGraphQL } from '@/app/_blocks/MusterGraphQL';
import { PageHeroGraphQL } from '@/app/_blocks/PageHeroGraphQL';
import { ProductHeroGraphQL } from '@/app/_blocks/ProductHeroGraphQL';
import { ProjectBlockGraphQL } from '@/app/_blocks/ProjectBlockGraphQL';
import { TeamBlockGraphQL } from '@/app/_blocks/TeamBlockGraphQL';
import { TextBlockGraphQL } from '@/app/_blocks/TextBlockGraphQL';
import { TextGalleryGraphQL } from '@/app/_blocks/TextGalleryGraphQL';
import { TextIframeBlockGraphQL } from '@/app/_blocks/TextIframeBlockGraphQL';
import { TopicsBlockGraphQL } from '@/app/_blocks/TopicsBlockGraphQL';
import { VideoBlockGraphQL } from '@/app/_blocks/VideoBlockGraphQL';

import { toKebabCase } from '@/app/_utilities/toKebabCase';
import { BackgroundColor } from '../BackgroundColor';
import { VerticalPadding, VerticalPaddingOptions } from '../VerticalPadding';

const blockComponents = {
  accordion: AccordionBlockGraphQL,
  'application-hero': ApplicationHeroGraphQL,
  'before-and-after': BeforeAndAfterGraphQL,
  'product-hero': ProductHeroGraphQL,
  'comparison-table': ComparisonTableGraphQL,
  content: ContentBlock,
  'features-block': FeaturesBlockGraphQL,
  'formBlock': FormBlockGraphQL,
  'gallery-block': GalleryBlockGraphQL,
  'gallery-page': GalleryPageGraphQL,
  'homepage-hero': HomepageHeroGraphQL,
  'iframe-block': IframeBlockGraphQL,
  kontact: KontactGraphQL,
  'prods-blk': MatchingProductsGraphQL,
  'page-hero': PageHeroGraphQL,
  mediaBlock: MediaBlock,
  muster: MusterGraphQL,
  'team': TeamBlockGraphQL,
  'text-block': TextBlockGraphQL,
  'text-gallery': TextGalleryGraphQL,
  'text-iframe-block': TextIframeBlockGraphQL,
  'topics-block': TopicsBlockGraphQL,
  'project-block': ProjectBlockGraphQL,
  'video-block': VideoBlockGraphQL,
};

export const Blocks: React.FC<{
  blocks: Page['layout'][0][];
  disableTopPadding?: boolean;
}> = props => {
  const { disableTopPadding, blocks } = props;

  const hasBlocks = blocks && Array.isArray(blocks) && blocks.length > 0;

  if (hasBlocks) {
    return (
      <Fragment>
        {blocks.map((block, index) => {
          const { blockName = `${Math.random()}`, blockType } = block;

          // Skip blocks hidden for the current locale
          if ('hideBlock' in block && block.hideBlock) {
            return null;
          }

          if (blockType && blockType in blockComponents) {
            const Block: React.FC<any> = blockComponents[blockType];

            // the cta block is containerized, so we don't consider it to be inverted at the block-level
            const blockIsInverted = 'invertBackground' in block ? block.invertBackground : false;
            const prevBlock = blocks[index - 1];

            const prevBlockInverted =
              prevBlock && 'invertBackground' in prevBlock && prevBlock?.invertBackground;

            const isPrevSame = Boolean(blockIsInverted) === Boolean(prevBlockInverted);

            const sectionMargin =
              'sectionMargin' in block ? (block as any).sectionMargin : null;

            let paddingTop: VerticalPaddingOptions = 'large';
            let paddingBottom: VerticalPaddingOptions = 'large';

            if (sectionMargin?.top) {
              paddingTop = sectionMargin.top;
            } else if (prevBlock && isPrevSame) {
              paddingTop = 'none';
            }

            if (sectionMargin?.bottom) {
              paddingBottom = sectionMargin.bottom;
            } else if (index === blocks.length - 1) {
              paddingBottom = 'large';
            }

            if (disableTopPadding && index === 0) {
              paddingTop = 'none';
            }

            if (Block) {
              return (
                <BackgroundColor key={index} invert={!!blockIsInverted}>
                  <VerticalPadding top={paddingTop} bottom={paddingBottom}>
                    <div>
                      <Block {...block} />
                    </div>
                  </VerticalPadding>
                </BackgroundColor>
              );
            }
          }
          return null;
        })}
      </Fragment>
    );
  }

  return null;
};
